import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import useTRSClusters from './hooks/useTRSClusters';
import useCreateTRSNode from './queries/useCreateTRSNode';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import {
    breadCrumbTrail,
    ERROR_MESSAGES,
    addHeader as header,
    initialState,
    trsNodesPath,
} from './constants';
import TRSNodeNameField from './formFields/TRSNodeNameField';
import TRSNodeClusterField from './formFields/TRSNodeClusterField';
import TRSNodeCapacityField from './formFields/TRSNodeCapacityField';

const TRSNodesAdd = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { darkMode } = useSelector((state) => state.settings);
    const [isDisabled, setIsDisabled] = useState(true);
    const { restError } = useSelector((state) => state.modal);

    // Get selected cluster from Index page
    const { clusterID = '' } = location.state || {};
    const statefulBreadCrumbTrail = breadCrumbTrail.map(
        ({ label, path: prevPath }) => ({
            label,
            path: [prevPath, { index: prevPath ? 0 : null, clusterID }],
        }),
    );

    const {
        data: clusters = [],
        isLoading: clustersLoading,
        isFetching: clustersFetching,
        isRefetching: clustersRefetching,
        error: clustersError,
    } = useTRSClusters();

    const handleSuccess = () => {
        toast.success('Create a TRS Node successfully!');
        navigate(trsNodesPath, { state: { clusterID } });
    };

    const handleError = (error) => {
        console.error('Failed to create TRS Node:', error);
        toast.error(
            restError ?? 'Failed to create a TRS Node. Please try again.',
        );
    };

    const { mutate: createNode, isPending } = useCreateTRSNode(
        handleSuccess,
        handleError,
    );

    const finalFormSubmit = async (values) => {
        if (!values.name || values.name.length < 2) {
            console.error(ERROR_MESSAGES.NODE_NAME_REQUIRED);
            return;
        }

        if (!values.clusterID) {
            console.error(ERROR_MESSAGES.CLUSTER_REQUIRED);
            return;
        }

        if (!values.capacity || values.capacity <= 0) {
            console.error(ERROR_MESSAGES.CAPACITY_INVALID);
            return;
        }

        const finalSubmit = {
            name: values.name,
            clusterID: values.clusterID,
            capacity: parseInt(values.capacity, 10),
        };

        createNode(finalSubmit);
    };

    const formFields = (errors, touched, values) => {
        const hasRequiredFields =
            values?.name &&
            values?.clusterID &&
            values?.capacity &&
            parseInt(values.capacity, 10) > 0;

        if (hasRequiredFields) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }

        const fields = [
            <TRSNodeNameField
                key="name"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <TRSNodeClusterField
                key="clusterID"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                clusters={clusters}
                isDisabled={isDisabled}
            />,
            <TRSNodeCapacityField
                key="capacity"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
        ];

        return fields;
    };

    const isFetchingData =
        clustersLoading || clustersRefetching || clustersFetching || isPending;

    // Handle loading errors
    if (clustersError) {
        toast.error('Failed to load clusters. Please refresh the page.');
    }

    if (isFetchingData) {
        return <LoadingIndicator />;
    }

    // Update initial state with pre-selected cluster if available
    const initialValues = {
        ...initialState,
        clusterID,
    };

    return (
        <FormBase
            isDisabled={isDisabled}
            breadCrumbTrail={statefulBreadCrumbTrail}
            header={header}
            initialValues={initialValues}
            finalFormSubmit={finalFormSubmit}
            backLink={[trsNodesPath, { state: { index: 0, clusterID } }]}>
            {({ errors, touched, values }) =>
                formFields(errors, touched, values)
            }
        </FormBase>
    );
};

export default TRSNodesAdd;
